import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Grid,
  Typography,
} from '@mui/material';
import { useUser } from '@/providers';

import CustomStartCommandCheckbox from './CustomStartCommandCheckbox';
import StartCommandField from './StartCommandField';
import RoutesHeader from './RoutesHeader';
import RouteList from './RouteList';
import AddRouteButton from './AddRouteButton';
import RegionalServicesButton from './RegionalServicesButton';
import ServicesDrawer from '@/components/shared/ServicesDrawer';
import cleanEmptyValues from '@/util/cleanEmptyValues';
import JOB_TYPES from '@/components/jobs/types';
import EndpointProjectAuthorizer from './EndpointProjectAuthorizer';
import EndpointLLM from './EndpointLLM';
import EndpointGuardrail from './EndpointGuardrail';

export default function EndpointFields({
  initialValues,
  initialModel,
  type,
  onChange,
  edit = false,
}) {
  const user = useUser();
  const is_llm_job = type === JOB_TYPES.LLM.value;
  const is_guardrail_job = type === JOB_TYPES.GUARDRAIL.value;

  const [custom_start_command, setCustomStartCommand] = useState(
    Boolean(initialValues && initialValues.start_command)
  );

  const [routes_errors, setRoutesErrors] = useState(
    initialValues && initialValues.routes
      ? new Array(initialValues.routes.length).fill({
          duplicate: false,
          error: false,
        })
      : []
  );

  const [routes_count, setRoutesCount] = useState(
    initialValues && initialValues.routes ? initialValues.routes.length : 0
  );

  // Transform initialValues: migrate from service_id to services array
  const transformInitialValues = (values) => {
    if (!values) {
      return {
        routes: [],
        start_command: '',
        services: [],
        authorizer: undefined,
      };
    }
    // If service_id exists, convert to services array format
    if (values.service_id && !values.services) {
      return {
        routes: values.routes || [],
        start_command: values.start_command || '',
        services: [{ id: values.service_id }],
        authorizer: values.authorizer,
      };
    }
    return {
      routes: values.routes || [],
      start_command: values.start_command || '',
      services: values.services || [],
      authorizer: values.authorizer,
    };
  };

  const [values, setValues] = useState(transformInitialValues(initialValues));
  const [llm_values, setLlmValues] = useState(initialValues?.llm || {});
  const [llm_error, setLlmError] = useState(false);
  const [guardrail_values, setGuardrailValues] = useState(
    initialValues?.guardrail || {},
  );
  const [guardrail_error, setGuardrailError] = useState(false);
  const [authorizer_error, setAuthorizerError] = useState(false);

  const [servicesDrawerOpen, setServicesDrawerOpen] = useState(false);
  const [servicesDrawerError, setServicesDrawerError] = useState(false);

  const isInitialMount = useRef(true);

  const routeDupCheck = (routes, routes_errors) => {
    const paths = routes.map((route) => {
      if (route.path) {
        return route.verb + route.path.toLowerCase();
      }
      return undefined;
    });
    const dup_route = paths.filter((e, i, a) => a.indexOf(e) !== i);
    const errors = routes.map((route, i) => {
      const error = { ...routes_errors[i] };
      if (route.path) {
        error.duplicate = dup_route.includes(
          route.verb + route.path.toLowerCase()
        );
      }
      return error;
    });
    return errors;
  };

  // Initial mount - call onChange
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      if (is_llm_job) {
        const cleanedData = cleanEmptyValues({
          services: values.services,
          llm: llm_values,
          authorizer: values.authorizer,
        });
        onChange('endpoint', cleanedData || {}, llm_error || authorizer_error);
      } else if (is_guardrail_job) {
        const cleanedData = cleanEmptyValues({
          services: values.services,
          guardrail: guardrail_values,
          authorizer: values.authorizer,
        });
        onChange(
          'endpoint',
          cleanedData || {},
          guardrail_error || authorizer_error,
        );
      } else {
        const cleanedData = cleanEmptyValues(values);
        onChange('endpoint', cleanedData || {}, !Boolean(values.routes.length));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Update onChange when values or errors change
  useEffect(() => {
    if (isInitialMount.current) {
      return;
    }

    if (is_llm_job) {
      const cleanedData = cleanEmptyValues({
        services: values.services,
        llm: llm_values,
        authorizer: values.authorizer,
      });
      onChange('endpoint', cleanedData || {}, llm_error || authorizer_error);
      return;
    }

    if (is_guardrail_job) {
      const cleanedData = cleanEmptyValues({
        services: values.services,
        guardrail: guardrail_values,
        authorizer: values.authorizer,
      });
      onChange(
        'endpoint',
        cleanedData || {},
        guardrail_error || authorizer_error,
      );
      return;
    }

    const routes = values.routes
      .map((route) => (!route.path ? undefined : route))
      .filter((i) => i);
    const route_error = new Set(
      routes_errors.map((error) => Object.values(error)).flat()
    );

    const endpointValues = {
      ...values,
      routes,
    };
    const cleanedData = cleanEmptyValues(endpointValues);

    const hasError =
      (!custom_start_command &&
        (route_error.has(true) || !Boolean(routes.length))) ||
      (custom_start_command && !values.start_command);

    onChange('endpoint', cleanedData || {}, hasError);
  }, [
    values,
    llm_values,
    llm_error,
    guardrail_values,
    guardrail_error,
    authorizer_error,
    routes_errors,
    custom_start_command,
    onChange,
    is_llm_job,
    is_guardrail_job,
  ]);

  // Sync ServicesDrawer error state
  useEffect(() => {
    // ServicesDrawer error is handled separately
  }, [servicesDrawerError]);

  const handleCustomStartCommandChange = (checked) => {
    setCustomStartCommand(checked);
    setRoutesErrors([]);
    setRoutesCount(0);
    setValues({
      ...values,
      start_command: checked ? values.start_command : '',
      routes: [],
    });
  };

  const handleStartCommandChange = (newValue) => {
    setValues({
      ...values,
      start_command: newValue,
    });
  };

  const handleRouteChange = (index, routeValue, routeError) => {
    const routes = [...values.routes];
    routes[index] = routeValue;
    const newRoutesErrors = routeDupCheck(routes, [...routes_errors]);
    newRoutesErrors[index] = { ...newRoutesErrors[index], error: routeError };
    setRoutesErrors(newRoutesErrors);
    setValues({
      ...values,
      routes,
    });
  };

  const handleAddRoute = () => {
    setRoutesCount(routes_count + 1);
    setRoutesErrors([
      ...routes_errors,
      {
        duplicate: false,
        error: false,
      },
    ]);
    setValues({
      ...values,
      routes: [
        ...values.routes,
        {
          path: '',
          verb: 'POST',
          file: '',
          function: '',
          body: [],
          positional: true,
        },
      ],
    });
  };

  const handleRemoveRoute = (index) => {
    const routes = [...values.routes];
    routes.splice(index, 1);
    const newRoutesErrors = routeDupCheck(routes, [...routes_errors]);
    newRoutesErrors.splice(index, 1);
    setRoutesErrors(newRoutesErrors);
    setRoutesCount(routes_count - 1);
    setValues({
      ...values,
      routes,
    });
  };

  const handleServicesDrawerClose = (newServices) => {
    setValues({
      ...values,
      services: newServices,
    });
  };

  const handleLlmChange = useCallback((next_value, has_error) => {
    setLlmValues(next_value || {});
    setLlmError(has_error);
  }, []);

  const handleCheckpointChange = useCallback(
    (next_value, has_error) => {
      onChange('model', next_value || {}, has_error);
    },
    [onChange],
  );

  const handleGuardrailChange = useCallback((next_value, has_error) => {
    setGuardrailValues(next_value || {});
    setGuardrailError(has_error);
  }, []);

  const handleAuthorizerChange = useCallback((next_value, has_error) => {
    setValues((prev) => ({ ...prev, authorizer: next_value }));
    setAuthorizerError(has_error);
  }, []);

  return (
    <Grid container spacing={2}>
      {is_llm_job ? (
        <Grid size={12}>
          <EndpointLLM
            initialValues={initialValues?.llm || {}}
            initialCheckpoints={initialModel?.checkpoints || []}
            onChange={handleLlmChange}
            onCheckpointChange={handleCheckpointChange}
            edit={edit}
          />
        </Grid>
      ) : is_guardrail_job ? (
        <Grid size={12}>
          <EndpointGuardrail
            initialValues={initialValues?.guardrail || {}}
            onChange={handleGuardrailChange}
            edit={edit}
          />
        </Grid>
      ) : (
        <>
          <Grid size={12}>
            <CustomStartCommandCheckbox
              value={custom_start_command}
              onChange={handleCustomStartCommandChange}
              edit={edit}
            />
          </Grid>

          {custom_start_command ? (
            <Grid size={12}>
              <StartCommandField
                value={values.start_command}
                onChange={handleStartCommandChange}
                edit={edit}
                error={!values.start_command}
              />
            </Grid>
          ) : (
            <>
              <Grid size={12}>
                <RoutesHeader />
              </Grid>
              <Grid size={12}>
                <RouteList
                  routes={values.routes}
                  routes_errors={routes_errors}
                  onRouteChange={handleRouteChange}
                  onRemoveRoute={handleRemoveRoute}
                  edit={edit}
                />
              </Grid>
              <Grid size={12}>
                <AddRouteButton onClick={handleAddRoute} edit={edit} />
              </Grid>
            </>
          )}
        </>
      )}

      <Grid size={12}>
        <EndpointProjectAuthorizer
          initialValues={initialValues?.authorizer}
          onChange={handleAuthorizerChange}
          edit={edit}
        />
      </Grid>

      <Grid size={12}>
        <Typography variant='body1' fontWeight='medium' sx={{ mb: 1 }}>
          Regional Services
        </Typography>
        <RegionalServicesButton
          onClick={() => setServicesDrawerOpen(true)}
          count={values.services?.length || 0}
          edit={edit || !user.cloudbender_enabled}
        />
        <ServicesDrawer
          open={servicesDrawerOpen}
          handleClose={() => setServicesDrawerOpen(false)}
          initialValues={values.services || []}
          setValue={handleServicesDrawerClose}
          error={servicesDrawerError}
          setError={setServicesDrawerError}
        />
      </Grid>
    </Grid>
  );
}

